import type { ExtractedDataItem, OcrPage, OcrResult } from '../types';
import { legacyViewerPage } from './legacy-viewer-page';

/**
 * Why a page has, or hasn't, data — three states the sheet used to collapse
 * into two.
 *
 * `extracted` — the owning run chunked it.
 * `empty`     — the owning run was ASSIGNED it and produced nothing. A blank
 *               scan, or a batch the model answered with zero items (the
 *               pipeline logs `OCR_BATCH_COMPLETED_EMPTY` and still counts the
 *               batch a success). Not a failure; nothing was raised.
 * `uncovered` — no run claims it. The owner is a fallback, and its status says
 *               nothing about this page.
 */
export type PageCoverage = 'extracted' | 'empty' | 'uncovered';

export interface ContainerPageRow {
  /** The result that speaks for this page — status, timings, actions. */
  result: OcrResult;
  page: OcrPage;
  /** 1-based position within the container's page list. */
  pageIndex: number;
  fields: ExtractedDataItem[];
  coverage: PageCoverage;
}

/** A result's own page list, or its anchor page for legacy rows without one. */
function resolvePages(result: OcrResult): OcrPage[] {
  if (result.pages && result.pages.length > 0) return result.pages;
  const fallback = legacyViewerPage(result);

  return fallback ? [fallback] : [];
}

/**
 * How loudly a status speaks for a page nothing extracted. A run that failed
 * explains an empty page; a run that completed without producing a single chunk
 * for it does not, and letting it label the page "completed" is how a failed
 * 16-page run came to read as completed from page 5 on.
 */
const STATUS_PRECEDENCE: Record<string, number> = {
  failed: 0,
  completed_with_errors: 1,
  paused: 2,
  processing: 3,
  pending: 4,
  skipped: 5,
  completed: 6
};

function precedenceOf(result: OcrResult): number {
  return STATUS_PRECEDENCE[result.status] ?? STATUS_PRECEDENCE.completed;
}

/** chunk_id → 1-based page number within the result's own page list. */
function chunkPageMap(result: OcrResult): Map<string, number> {
  const map = new Map<string, number>();
  for (const chunk of result.chunks ?? []) {
    map.set(chunk.chunk_id, chunk.page_number);
  }
  return map;
}

/** Fields bucketed by the page their first reference chunk resolves to.
 * Refless fields fall back to page 1 so none are dropped. */
function fieldsByPage(result: OcrResult): Map<number, ExtractedDataItem[]> {
  const chunkPage = chunkPageMap(result);
  const buckets = new Map<number, ExtractedDataItem[]>();

  for (const field of result.extracted_data ?? []) {
    const firstRef = field.references?.[0];
    const pageNumber = firstRef ? (chunkPage.get(firstRef) ?? 1) : 1;
    const bucket = buckets.get(pageNumber) ?? [];
    bucket.push(field);
    buckets.set(pageNumber, bucket);
  }

  return buckets;
}

/**
 * One row per PHYSICAL page of a container.
 *
 * Every result repeats the container's whole page list — the API serializes the
 * full workload assignment onto each row — so exploding results × pages listed
 * each page once per result: a 16-page container with 13 results rendered 208
 * rows, and its progress read "16/208 pages".
 *
 * A page is spoken for by the result that actually extracted it (its chunks
 * resolve to that page), then by the run that was DISPATCHED to read it
 * (`assigned_page_numbers`), then by a result anchored to it (the `skipped`
 * duplicate-accounting rows), and only then by the run that merely covered it —
 * which is what keeps the pages of a failed run visible instead of vanishing
 * because nothing produced chunks for them. Ties go to the highest id, matching
 * how `OcrService::getResultsForContainer` picks per page, so this sheet cannot
 * disagree with the page the operator opens.
 *
 * The assignment step is load-bearing, not a nicety. Without it a page that
 * produced nothing had no owner at all and fell through to `spokesman`, which
 * deliberately picks the WORST status in the container — so a single refusal on
 * page 8 rendered pages 7 and 9-12 as failed, when all five had merely come back
 * empty from a run that completed. The fallback is still correct for a legacy
 * row that carries no assignment; it was only ever wrong as a first resort.
 */
export function buildContainerPageRows(
  results: OcrResult[]
): ContainerPageRow[] {
  if (results.length === 0) return [];

  // Physical pages, deduped by id, in document order.
  const pages: OcrPage[] = [];
  const seen = new Set<number>();
  for (const result of results) {
    for (const page of resolvePages(result)) {
      if (seen.has(page.id)) continue;
      seen.add(page.id);
      pages.push(page);
    }
  }

  const byResult = results.map((result) => ({
    result,
    pages: resolvePages(result),
    fields: fieldsByPage(result),
    // Coverage is what the result CHUNKED, not what it surfaced as fields: a
    // page can be extracted and yield no field, and a refless field is bucketed
    // onto page 1 without saying anything about page 1.
    extracted: new Set((result.chunks ?? []).map((c) => c.page_number)),
    assigned: new Set(result.assigned_page_numbers ?? [])
  }));

  const newestFirst = [...byResult].sort((a, b) => b.result.id - a.result.id);

  // Fallback owner: the covering run whose outcome best explains an empty page.
  //
  // Drawn ONLY from runs of unknown scope. A run that declares an assignment has
  // already said which pages are its concern, and letting it answer for one it
  // excluded contradicts its own declaration — which is the whole reason a
  // refusal on page 8 was rendered onto pages 7 and 9-12. When every run
  // declares, no spokesman is honest, so the newest is used purely as a handle
  // for the row and `coverage: 'uncovered'` tells the caller not to read its
  // status as this page's outcome.
  const undeclared = newestFirst.filter(
    (entry) => !Array.isArray(entry.result.assigned_page_numbers)
  );
  const spokesman =
    [...undeclared].sort(
      (a, b) => precedenceOf(a.result) - precedenceOf(b.result)
    )[0] ?? newestFirst[0];

  return pages.map((page, index) => {
    const pageNumber = index + 1;

    const producer = newestFirst.find(
      (entry) => entry.extracted.has(pageNumber) || entry.fields.has(pageNumber)
    );
    const assignee = newestFirst.find((entry) =>
      entry.assigned.has(pageNumber)
    );

    const owner =
      producer ??
      assignee ??
      newestFirst.find((entry) => entry.result.page_id === page.id) ??
      spokesman;

    const coverage: PageCoverage = producer
      ? 'extracted'
      : assignee
        ? 'empty'
        : 'uncovered';

    // The owner addresses the page by its position in the owner's OWN list,
    // which is the container list in every case but a legacy single-page row.
    const ownerPageNumber =
      owner.pages.findIndex((p) => p.id === page.id) + 1 || pageNumber;

    return {
      result: owner.result,
      page,
      pageIndex: pageNumber,
      fields: owner.fields.get(ownerPageNumber) ?? [],
      coverage
    };
  });
}
